import { createSelector } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { chain, maxBy, memoize, takeRight, takeRightWhile } from 'lodash';
import { selectMessageConversationById, selectState } from './message-conversation.selectors';
import { MessageData, MessageDirection } from '../models/message-data.model';
import { Participant } from '../../common/models/participant.model';
import { ExtendedMessageData } from '../models/extended-message-data.model';
import { messageEntityAdapter } from '../adapters/message.adapter';
import { ParticipantPurpose } from 'common/store/genesys-cloud/conversations/common/models/participant-purpose.enum';
import { MessageDataSenderType } from 'common/store/genesys-cloud/conversations/common/models/message-data-sender-type.enum';
import { MessageType } from '../../common/models/message-type.enum';
import moment from 'moment';

const { selectIds, selectEntities, selectAll } = messageEntityAdapter.getSelectors();

const selectMessagesSubState = createSelector(selectState, (state) => state.messages);
const selectMessageSubStateByConversationId = memoize((conversationId: string) =>
  createSelector(selectMessagesSubState, (state) => state[conversationId])
);

export const selectMessageIdsByConversationId = memoize((conversationId: string) =>
  createSelector(selectMessageSubStateByConversationId(conversationId), (subState) =>
    subState ? <string[]>selectIds(subState) : []
  )
);

export const selectMessageCountByConversationId = memoize((conversationId: string) =>
  createSelector(selectMessageIdsByConversationId(conversationId), (messages) => messages.length)
);

const selectMessageEntitiesByConversationId = memoize((conversationId: string) =>
  createSelector(selectMessageSubStateByConversationId(conversationId), (subState) =>
    subState ? selectEntities(subState) : {}
  )
);

const getExtendedMessageDataFrom = (
  participant: Participant,
  messageEntities: Dictionary<MessageData>,
  currentAgentParticipantId: string
): ExtendedMessageData[] =>
  chain(participant.messages)
    .flatMap((message) => message.messages)
    .filter(Boolean)
    .filter((messageDetails) => !!messageEntities[messageDetails.messageId])
    .map((messageDetails) => ({
      ...messageEntities[messageDetails.messageId],
      id: messageDetails.messageId,
      timestamp: messageDetails.messageTime,
      status: messageDetails.messageStatus,
      senderType: getSenderType(participant.purpose, participant.userId, currentAgentParticipantId),
      senderId: participant.userId
    }))
    .value();

export const selectExtendedMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(
    selectMessageConversationById(conversationId),
    selectMessageEntitiesByConversationId(conversationId),
    (conversation, messageEntities): ExtendedMessageData[] =>
      conversation && messageEntities
        ? chain(conversation.participants)
            .filter((participant) => participant.purpose !== ParticipantPurpose.ACD)
            .flatMap((participant) =>
              getExtendedMessageDataFrom(participant, messageEntities, conversation.agentParticipant.userId)
            )
            .sortBy('timestamp')
            .value()
        : []
  )
);

export const selectAllMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(selectMessageSubStateByConversationId(conversationId), (subState) =>
    subState ? selectAll(subState) : []
  )
);

export const selectLastTwoMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(selectAllMessagesByConversationId(conversationId), (messages) => takeRight(messages, 2))
);

const selectInboundMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(selectAllMessagesByConversationId(conversationId), (messages) =>
    messages.filter((m) => m.direction === MessageDirection.Inbound)
  )
);

export const selectLastInboundMessageByConversationId = memoize((conversationId: string) =>
  createSelector(selectInboundMessagesByConversationId(conversationId), (messages) =>
    messages.length ? messages[messages.length - 1] : null
  )
);

const selectExtendedOutboundMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(selectExtendedMessagesByConversationId(conversationId), (messages) =>
    messages.filter((m) => m.direction === MessageDirection.Outbound)
  )
);

export const selectLastExtendedOutboundMessageByConversationId = memoize((conversationId: string) =>
  createSelector(selectExtendedOutboundMessagesByConversationId(conversationId), (messages) =>
    messages.length ? messages[messages.length - 1] : null
  )
);

export const selectRemainingTimeToRespondMessageByConversationId = memoize((conversationId: string) =>
  createSelector(selectLastInboundMessageByConversationId(conversationId), (message) =>
    message ? getRemainingTimeToRespondMessage(message) : Number.POSITIVE_INFINITY
  )
);

export const selectDeploymentIdByConversationId = memoize((conversationId: string) =>
  createSelector(selectLastInboundMessageByConversationId(conversationId), (message) => getDeploymentId(message))
);

const selectUnansweredMessagesByConversationId = memoize((conversationId: string) =>
  createSelector(selectAllMessagesByConversationId(conversationId), (messages) =>
    takeRightWhile(messages, (message) => message.direction === MessageDirection.Inbound)
  )
);

export const selectUnansweredMessageCountByConversationId = memoize((conversationId: string) =>
  createSelector(selectUnansweredMessagesByConversationId(conversationId), (messages) => messages.length)
);

export const selectMessageById = memoize(
  (conversationId, messageId) =>
    createSelector(
      selectMessageEntitiesByConversationId(conversationId),
      (messageEntities) => messageEntities[messageId]
    ),
  memoizeByMessageId
);

export const selectLastMessageStatusById = memoize(
  (conversationId: string, messageId: string) =>
    createSelector(selectMessageById(conversationId, messageId), (message) => {
      return maxBy(message?.normalizedReceipts, (r) => r.channel?.time)?.status;
    }),
  memoizeByMessageId
);

function getSenderType(
  participantPurpose: ParticipantPurpose,
  participantId: string,
  activeParticipantId: string
): MessageDataSenderType {
  const isDifferentAgent = participantPurpose === ParticipantPurpose.Agent && participantId !== activeParticipantId;
  const isWorkFlow = participantPurpose === ParticipantPurpose.Workflow;
  if (isDifferentAgent) {
    return MessageDataSenderType.OtherAgent;
  } else if (participantId === activeParticipantId) {
    return MessageDataSenderType.ActualAgent;
  } else if (participantPurpose === ParticipantPurpose.Customer) {
    return MessageDataSenderType.Customer;
  } else if (isWorkFlow) {
    return MessageDataSenderType.Bot;
  } else {
    throw new TypeError(`Unsupported or Unknown Participant Purpose: ${participantPurpose}`);
  }
}

const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
const oneWeekInMilliseconds = oneDayInMilliseconds * 7;

function getRemainingTimeToRespondMessage(message: MessageData): number {
  const differenceInMilliseconds = moment().diff(moment(message.timestamp), 'milliseconds');

  if (message.messengerType === MessageType.WhatsApp) {
    return oneDayInMilliseconds - differenceInMilliseconds;
  } else if (message.messengerType === MessageType.Facebook) {
    return oneWeekInMilliseconds - differenceInMilliseconds;
  } else {
    return Number.POSITIVE_INFINITY;
  }
}

export function memoizeByMessageId(conversationId: string, messageId: string): string {
  return messageId;
}

function getDeploymentId(message: MessageData): string | undefined {
  const deploymentType = 'deployment';
  if (message?.normalizedMessage?.channel?.to?.additionalIds?.length) {
    const deploymentId = message.normalizedMessage.channel.to.additionalIds.find(
      (a) => a.type.toLocaleLowerCase() === deploymentType
    )?.value;
    return deploymentId;
  }
  return undefined;
}
